import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import isEmpty from "lodash/isEmpty";
import LegacyTooltip from "../../../CustomOverlayTrigger/LegacyTooltip";
import OverlayTrigger from "../../../CustomOverlayTrigger/CustomOverlayTrigger";

import {
  HASHTAG_FEED_GET_HASHTAG_MEDIA_ROUTINE,
  LOAD_MORE_IG_ASSETS_ROUTINE,
} from "constants/ActionTypes";
import { DEFAULT_HASHTAG_FILTER, PAGES } from "constants/MediaLibrary";
import FeedHeader from "components/MediaLibrary/SourceMedia/FeedHeader";
import HashtagFeedActions from "components/MediaLibrary/SourceMedia/HashtagFeed/HashtagFeedActions";
import FeedGrid from "components/MediaLibrary/SourceMedia/FeedGrid/FeedGrid";
import EmptyContainer from "components/MediaLibrary/EmptyContainer/EmptyContainer";
import { FeedSelectedAssets } from "components/MediaLibrary/SourceMedia/FeedSelectedAssets/FeedSelectedAssets";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { FullPageLoading } from "@/components/ui/full-page-loading";
import {
  getSelectedIgAccount,
  getSelectedHashtag,
  getInvalidIgAccount,
  getAllMediaLoaded,
  getFeedMedia,
  getSelectedFeedItems,
} from "selectors/hashtagFeedSelectors";
import { loadMoreIgAssets } from "actions/sourceMedia";
import { getLoading, getRoutinesLoading } from "selectors/commonSelectors";

export function useHashtagFeed() {
  const feedMedia = useSelector((state) => getFeedMedia(state, PAGES.HASHTAGS));
  const selectedIgAccount = useSelector(getSelectedIgAccount);
  const invalidIgAccount = useSelector(getInvalidIgAccount);
  const selectedHashtag = useSelector(getSelectedHashtag);
  const loading = useSelector(getLoading);
  const loadingInitialMedia = useSelector((state) =>
    getRoutinesLoading(state, [HASHTAG_FEED_GET_HASHTAG_MEDIA_ROUTINE])
  );

  const [filterState, setFilterState] = useState(DEFAULT_HASHTAG_FILTER);

  // NOTE: (Andrey) reset filter state when selected hashtag
  useEffect(() => {
    selectedIgAccount &&
      setFilterState({
        ...DEFAULT_HASHTAG_FILTER,
        excludeAccounts: [selectedIgAccount._id],
      });
  }, [selectedIgAccount]);

  return {
    feedMedia,
    selectedIgAccount,
    invalidIgAccount,
    selectedHashtag,
    loading,
    loadingInitialMedia,
    filterState,
    setFilterState,
  };
}

const HashtagFeed = () => {
  const {
    feedMedia,
    selectedIgAccount,
    invalidIgAccount,
    selectedHashtag,
    loadingInitialMedia,
    filterState,
    setFilterState,
  } = useHashtagFeed();
  const selectedFeedItems = useSelector(getSelectedFeedItems);

  // Track if we've ever loaded content (for distinguishing initial load vs subsequent loads)
  const hasEverLoadedRef = useRef(false);
  if (feedMedia.length > 0 || selectedHashtag) {
    hasEverLoadedRef.current = true;
  }

  const headerText = !isEmpty(selectedHashtag)
    ? `#${selectedHashtag.name}`
    : "Unknown";

  // Show full page loading only on the very first load (before any content has ever been shown)
  const showFullPageLoading = loadingInitialMedia && !hasEverLoadedRef.current;
  // Show inline loading overlay for all subsequent loads (refresh, switching hashtags, etc.)
  const showContentLoading = loadingInitialMedia && hasEverLoadedRef.current;

  return (
    <div className="flex flex-col h-full relative w-full">
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex-1 overflow-auto min-h-0 px-8">
          {showFullPageLoading && <FullPageLoading />}
          {selectedIgAccount && selectedHashtag && !showFullPageLoading && (
            <>
              <FeedHeader headerText={headerText} />
              <HashtagFeedActions
                filterState={filterState}
                setFilterState={setFilterState}
              />
              <div className="relative">
                {showContentLoading && (
                  <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center min-h-[200px]">
                    <Spinner className="h-8 w-8 text-blue-500" />
                  </div>
                )}
                <FeedGrid feedMedia={feedMedia} />
              </div>
              <LoadMore filterState={filterState} />
            </>
          )}
          {selectedIgAccount && invalidIgAccount && !loadingInitialMedia && (
            <EmptyContainer>
              <div className="text-red-400 text-center">
                <div>
                  The selected account @{selectedIgAccount.login} does not have
                  the correct credentials.
                </div>
                <div>Please reconnect Sked Insights on the accounts page.</div>
              </div>
            </EmptyContainer>
          )}
          {selectedIgAccount &&
            !invalidIgAccount &&
            !selectedHashtag &&
            !loadingInitialMedia && (
              <EmptyContainer>Please select a hashtag.</EmptyContainer>
            )}
        </div>

        {/* Bottom drawer - only when items selected */}
        {selectedFeedItems.length > 0 && (
          <div className="border-t border-neutral-200 shadow-[0px_-4px_12px_rgba(0,0,0,0.08)] bg-white min-h-40 z-10">
            <FeedSelectedAssets />
          </div>
        )}
      </div>
    </div>
  );
};

const LoadMore = ({ filterState }) => {
  const dispatch = useDispatch();
  const isLoadingMore = useSelector((state) =>
    getRoutinesLoading(state, [LOAD_MORE_IG_ASSETS_ROUTINE])
  );
  const allMediaLoaded = useSelector((state) =>
    getAllMediaLoaded(state, PAGES.HASHTAGS)
  );

  const tooltipJsx = (
    <LegacyTooltip id="all-media-loaded">
      Showing all posts from within the last 24 hours.
    </LegacyTooltip>
  );

  return (
    <div className="flex flex-col items-center justify-center mt-4 mb-8">
      {allMediaLoaded && (
        <OverlayTrigger placement="bottom" overlay={tooltipJsx}>
          <div className="mb-4 text-gray-400 cursor-default">
            All posts have been loaded
          </div>
        </OverlayTrigger>
      )}
      <Button
        variant="black-secondary"
        size="md"
        disabled={isLoadingMore || allMediaLoaded}
        onClick={() => {
          if (!isLoadingMore) {
            dispatch(loadMoreIgAssets(filterState));
          }
        }}
      >
        {isLoadingMore ? <Spinner className="h-4 w-4" /> : "Load More"}
      </Button>
    </div>
  );
};

export default HashtagFeed;
